import { useEffect, useState, useCallback } from 'react'
import { supabase } from '@/integrations/supabase/client'
import type { RealtimeChannel } from '@supabase/supabase-js'
import { toast } from 'sonner'

export interface Notification {
  id: string
  type: string
  title: string
  message: string
  link?: string
  read_at?: string
  created_at: string
  metadata: Record<string, unknown>
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * Keep provider-shaped notification rows out of the UI boundary. The panel
 * only needs bounded scalar identity/content and an object metadata envelope;
 * unknown rows are dropped rather than rendered as object text or invalid
 * dates.
 */
export function normalizeNotification(value: unknown): Notification | null {
  if (!isRecord(value)) return null

  const id = typeof value.id === 'string' ? value.id.trim() : ''
  const type = typeof value.type === 'string' ? value.type.trim() : ''
  const title = typeof value.title === 'string' ? value.title.trim() : ''
  const message = typeof value.message === 'string' ? value.message.trim() : ''
  const createdAt = typeof value.created_at === 'string' ? value.created_at : ''

  if (!id || !type || !title || !message || !createdAt || !Number.isFinite(Date.parse(createdAt))) {
    return null
  }

  const readAt = typeof value.read_at === 'string' && value.read_at.trim()
    ? value.read_at
    : undefined
  const link = typeof value.link === 'string' && value.link.trim()
    ? value.link
    : undefined

  return {
    id,
    type,
    title,
    message,
    created_at: createdAt,
    read_at: readAt,
    link,
    metadata: isRecord(value.metadata) ? value.metadata : {},
  }
}

/** A sale is worth interrupting for (#455). The id keeps two mounted bells from toasting twice. */
export function announceSale(n: Notification): void {
  if (n.type !== 'purchase' || n.metadata.role !== 'seller') return
  toast.success(n.title, { id: `notification-${n.id}`, description: n.message })
}

export function normalizeNotifications(value: unknown): Notification[] {
  if (!Array.isArray(value)) return []
  return value.flatMap((row) => {
    const notification = normalizeNotification(row)
    return notification ? [notification] : []
  })
}

export function useNotifications() {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const loadNotifications = useCallback(async () => {
    try {
      setError(null)
      setLoading(true)
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) { setNotifications([]); setUnreadCount(0); return }

      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50)

      if (error) throw error

      const list = normalizeNotifications(data)
      setNotifications(list)
      setUnreadCount(list.filter(n => !n.read_at).length)
    } catch (err) {
      setError(err as Error)
    } finally {
      setLoading(false)
    }
  }, [])

  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      setError(null)
      const { error } = await supabase.functions.invoke('mark-notification-read', { body: { notificationId } })
      if (error) throw error
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, read_at: new Date().toISOString() } : n))
      setUnreadCount(prev => Math.max(0, prev - 1))
    } catch (err) {
      setError(err as Error)
    }
  }, [])

  const markAllAsRead = useCallback(async () => {
    try {
      setError(null)
      const { error } = await supabase.functions.invoke('mark-all-notifications-read')
      if (error) throw error
      const now = new Date().toISOString()
      setNotifications(prev => prev.map(n => ({ ...n, read_at: n.read_at || now })))
      setUnreadCount(0)
    } catch (err) {
      setError(err as Error)
    }
  }, [])

  useEffect(() => {
    loadNotifications()

    let channel: RealtimeChannel | undefined
    const setup = async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return
      channel = supabase
        .channel(`notifications:${user.id}`)
        .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`
        }, (payload) => {
          const n = normalizeNotification(payload.new)
          if (!n) return
          announceSale(n)
          setNotifications(prev => [n, ...prev])
          setUnreadCount(prev => prev + (n.read_at ? 0 : 1))
        })
        .subscribe()
    }
    setup()

    return () => { channel?.unsubscribe() }
  }, [loadNotifications])

  return { notifications, unreadCount, loading, error, markAsRead, markAllAsRead, refresh: loadNotifications }
}

